import {
  initProStudioUrqlGql,
  SolutionEnvironmentsEnum,
} from '@pypestream/api-services/urql';
import { reportError, throwErrorWithId } from '@pypestream/utils';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import {
  updateSurveyBotStreams,
  UpdateSurveyBotStreamsParams,
} from './helpers/solutions';
import { checkForErrors } from '../../utils/check-for-errors';
import {
  readLegacyBotSolutionId,
  resolveBotSolutionId,
} from '../../utils/resolve-bot-solution-id';
import { readVersionName } from '../../utils/build-selected-solution';
import {
  readAgentCompilerMode,
  shouldPublishWithBotCompiler,
} from '../../utils/agent-compiler-mode';
import {
  captureAnalyticsEvent,
  classifyAnalyticsError,
  solutionAnalyticsProps,
  splitSupportErrorId,
  MAX_ANALYTICS_ERROR_MESSAGE_LENGTH,
} from '../../utils/analytics';

/**
 * Where a publish attempt died. `solution_published + solution_published_failed`
 * is only the number of publish attempts if EVERY entrance reports, so each
 * failure exit of this invoke carries one of these.
 *
 * NOT covered, and deliberately so: the two pre-flight refusals in
 * `publish-modal.tsx` — a rejected `waitForPendingSaves` and a
 * `resolvePublishBlock` hit (a duplicate or missing node number, the most
 * frequent publish refusal in this code). Both show a publish-failed toast and
 * `return` without ever dispatching `publishBot`, so this invoke cannot see
 * them; instrumenting them means rebuilding the identity payload in a
 * component that has none of it assembled. They are a separate change, and
 * until it lands the failure counts here under-report by those two classes.
 */
type PublishFailureStage =
  | 'guard'
  | 'survey_streams'
  | 'mutation'
  | 'publish_response';

/**
 * Every exit settles the caller. `PublishModal` holds the editor in view mode until
 * `onPublishComplete` answers (the short snapshot lock), and nothing else lifts it any
 * more — a publish no longer makes the watcher restore the mode. So a throw anywhere
 * before the mutation answers (missing context, a failed survey-link write) reports an
 * error first and is rethrown for the machine's own error routing.
 */
export const publishBotVersion = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<void> => {
  if (event.type !== 'publishBot') {
    return;
  }

  try {
    await publishBotVersionUnsettled(ctx, event);
  } catch (error) {
    event.payload.onPublishComplete({
      status: 'error',
      solutionVersionId: null,
      errorMessage:
        error instanceof Error ? error.message : 'Error publishing solution',
    });
    throw error;
  }
};

const publishBotVersionUnsettled = async (
  ctx: ProStudioContext,
  event: Extract<ProStudioEvents, { type: 'publishBot' }>
): Promise<void> => {
  const {
    botName: ctxBotName,
    customerId,
    project,
    solutions: bots,
    userInfo,
    draftVersionSolutionData,
    solutionVersions,
    featureFlags,
  } = ctx;

  const projectId = project?.id;

  const {
    version,
    republish,
    env: environment,
    botType,
    bots: eventBots,
  } = event.payload;

  /**
   * The one place a `solution_published_failed` payload is built, so that every
   * failure exit reports the same shape.
   *
   * The identity fields are read defensively because the guards below fire
   * exactly when one of them is missing — an event with `customer_id:
   * undefined` still counts the attempt, which is the whole point.
   *
   * Called BEFORE the `throwErrorWithId` / rethrow beside it rather than after,
   * because those do not return: there is no "after". That is safe here and is
   * not a contradiction of "serve the user first" — `captureAnalyticsEvent`
   * wraps its whole body in `try/catch`, and every argument expression below is
   * a literal, an optional chain or a `String()`, so none of them can throw.
   */
  const captureFailure = (
    failureStage: PublishFailureStage,
    rawMessage: string,
    attemptedVersionId: string | null = null
  ): void => {
    const { message, errorId } = splitSupportErrorId(rawMessage);
    const sentMessage = message.slice(0, MAX_ANALYTICS_ERROR_MESSAGE_LENGTH);

    captureAnalyticsEvent('solution_published_failed', {
      ...solutionAnalyticsProps(ctx),
      attempted_version_id: attemptedVersionId,
      publish_environment: environment,
      republish: Boolean(republish),
      failure_stage: failureStage,
      error_kind: classifyAnalyticsError(sentMessage),
      error_message: sentMessage,
      error_id: errorId,
    });
  };

  const toMessage = (error: unknown, fallback: string): string =>
    error instanceof Error ? error.message : fallback;

  if (!projectId) {
    captureFailure('guard', 'Project ID is required');
    throwErrorWithId('Project ID is required');
    return;
  }

  if (
    !userInfo?.id ||
    !userInfo?.email ||
    !userInfo?.firstName ||
    !userInfo?.lastName
  ) {
    captureFailure('guard', 'User info is required');
    throwErrorWithId('User info is required');
    return;
  }

  if (!ctxBotName) {
    captureFailure('guard', 'Bot Name is required');
    throwErrorWithId('Bot Name is required');
    return;
  }

  if (!customerId) {
    captureFailure('guard', 'Customer ID is required');
    throwErrorWithId('Customer ID is required');
    return;
  }

  const uniqueCustomerId = project?.projectProStudioSetting?.customerId;

  if (!uniqueCustomerId) {
    captureFailure('guard', 'Customer ID is required');
    throwErrorWithId('Customer ID is required');
    return;
  }

  if (!version) {
    captureFailure('guard', 'Version is required');
    throwErrorWithId('Version is required');
    return;
  }

  const botId = `${customerId}.${ctxBotName}`;

  const urqlGql = initProStudioUrqlGql({
    userId: userInfo.id,
    userEmail: userInfo.email,
    userFirstName: userInfo.firstName,
    userLastName: userInfo.lastName,
  });

  if (botType === 'survey') {
    // The survey's own start-chat id is what a linked main's streams carry as
    // `end_chat_bot_id` — the same `<customerName>.<uuid>` the backend stamps as
    // `start_chat_bot_id` on the survey's own channels.
    const surveyStartChatId = resolveBotSolutionId(ctx);
    if (!surveyStartChatId) {
      captureFailure('guard', 'Survey solution ID is required');
      throwErrorWithId('Survey solution ID is required');
      return;
    }

    try {
      await updateSurveyBotStreams({
        bots,
        eventBots,
        environment,
        uniqueCustomerId,
        projectId,
        botId: surveyStartChatId,
        customerName: customerId,
        userInfo: userInfo as UpdateSurveyBotStreamsParams['userInfo'],
      });
    } catch (error) {
      // Rethrown unchanged: this rejection is what the machine's `onError`
      // turns into the user's toast, and the capture must not alter it.
      captureFailure(
        'survey_streams',
        toMessage(error, 'Error updating survey bot streams')
      );
      throw error;
    }
  }

  const botSolutionVersionId = `${botId}.${version}`;

  // Republish is asked for by version NAME — that is what the Versions page history rows
  // carry (`buildVersionHistory` reads `solutionVersion.name`). Matching on the legacy
  // `botSolutionVersionId` composite alone finds nothing for a v2 solution, where the field
  // is null on every version, so republish reported "no solution version available" for
  // every migrated solution. The composite is kept as the fallback for legacy versions,
  // whose `name` is null and only recoverable from it (see `readVersionName`).
  const getRepublishVersionId = () => {
    const matchingVersion = solutionVersions.find(
      (candidate) =>
        (candidate && readVersionName(candidate) === version) ||
        candidate?.botSolutionVersionId === botSolutionVersionId
    );
    return matchingVersion?.id;
  };

  const environmentName = environment.toLowerCase() as SolutionEnvironmentsEnum;

  // Draft-only versioning: a publish is asked for by the DRAFT, which the backend
  // snapshots onto a version slot and never moves, and a republish retries an existing
  // DEPLOYMENT — a published slot is immutable, so there is nothing to snapshot. Neither
  // takes the old `solutionVersionId` + `doVersionBump` pair any more.
  let solutionVersionId: string | undefined;
  let publishResponse: Parameters<typeof checkForErrors>[0];

  try {
    if (republish) {
      solutionVersionId = getRepublishVersionId();

      const deploymentResponse = solutionVersionId
        ? await urqlGql.getSolutionDeploymentForVersion({
            solutionVersionId,
            environment: environmentName,
          })
        : undefined;
      const solutionDeploymentId =
        deploymentResponse?.data?.tenancy_solution_deployments?.[0]?.id;

      // The backend republishes a deployment in the environment it already ran in; a
      // version that was never deployed to the chosen environment cannot be moved there.
      if (!solutionVersionId || !solutionDeploymentId) {
        const errorMessage = solutionVersionId
          ? `Version ${version} was never deployed to ${environmentName}, so it cannot be republished there`
          : 'No solution version available to republish';
        captureFailure('guard', errorMessage, solutionVersionId ?? null);
        event.payload.onPublishComplete({
          status: 'error',
          solutionVersionId: solutionVersionId ?? null,
          errorMessage,
        });
        return;
      }

      publishResponse = await urqlGql.republishSolutionDeployment({
        solutionDeploymentId,
      });
    } else {
      solutionVersionId = draftVersionSolutionData?.id;

      if (!solutionVersionId) {
        const errorMessage = 'No draft version available to publish';
        captureFailure('guard', errorMessage);
        event.payload.onPublishComplete({
          status: 'error',
          solutionVersionId: null,
          errorMessage,
        });
        return;
      }

      // Same `ps-agent-compiler-errors` flag the table already validates against, also
      // told to the backend so the build agrees with what the editor has been showing.
      // Sent unconditionally: the argument is what pins the choice, and leaving it out
      // would silently fall back to the backend default.
      publishResponse = await urqlGql.publishSolutionVersion({
        draftVersionId: solutionVersionId,
        environment: environmentName,
        // Only a legacy solution can be built by the Bot Compiler: it reads Bot
        // Manager's `bot.csv`, which a v2 solution never had. Validation already
        // ignores the kill switch for v2 (`validateAndSync`), and publish must agree
        // with what validation checked.
        useBotCompiler:
          Boolean(readLegacyBotSolutionId(ctx)) &&
          shouldPublishWithBotCompiler(readAgentCompilerMode(featureFlags)),
      });
    }
  } catch (error) {
    // A mutation that REJECTS (transport failure, an aborted request) never
    // reaches `checkForErrors` below, so its own catch cannot see it.
    captureFailure(
      'mutation',
      toMessage(error, 'Error publishing solution version'),
      solutionVersionId ?? null
    );
    throw error;
  }

  try {
    checkForErrors(publishResponse);
  } catch (error) {
    // PSTUDIO-4194 criterion I. This catch is why the publish failure of
    // 2026-09-19 reached the user as a toast and reached Sentry not at all:
    // the error is reduced to a string for the modal and the object is dropped
    // here. The shared `createInvokablePromise` reporter cannot see it either —
    // this invoke RESOLVES, so there is no rejection for `onError` to catch.
    //
    // The `extra` fields are identifiers only — no node content and nothing
    // from the draft. The `error` itself is NOT so bounded, and saying it was
    // would be a false claim about a privacy boundary: its `message` comes from
    // `checkForErrors`, which prefers the backend's own text
    // (`extensions.internal.response.body.errors`) over the generic one, so a
    // constraint violation can carry a value from the row that broke it. What
    // bounds it is the SDK: `extraErrorDataIntegration` is not among the
    // defaults, so `AppError.context` — which holds the whole serialized
    // response — does not travel, and `maxValueLength` truncates the message at
    // 250 characters.
    // Guarded: a throw here would skip `onPublishComplete` below, so the invoke
    // would reject instead of resolving, the modal would never learn the
    // publish failed, and the machine would take its generic error path — the
    // telemetry deciding the user-facing outcome.
    try {
      reportError(error, {
        source: 'publish-bot-version',
        type: 'publish',
        tags: {
          app: 'pro-studio',
          publishEnvironment: environmentName,
        },
        extra: {
          botSolutionVersionId,
          attemptedVersionId: solutionVersionId,
          republish: Boolean(republish),
        },
      });
    } catch {
      // Reporting is best-effort; the user-facing result below is not.
    }

    const errorMessage = toMessage(error, 'Error publishing solution version');

    captureFailure('publish_response', errorMessage, solutionVersionId ?? null);

    // The modal owns the user-facing toast copy (it has the translation hook).
    event.payload.onPublishComplete({
      status: 'error',
      solutionVersionId,
      errorMessage,
    });
    return;
  }

  captureAnalyticsEvent('solution_published', {
    ...solutionAnalyticsProps(ctx),
    // The draft id for a publish (the slot is minted server-side and not returned),
    // the published slot's id for a republish.
    solution_version_id: solutionVersionId ?? null,
    publish_environment: environment,
    republish: Boolean(republish),
  });

  event.payload.onPublishComplete({
    status: 'success',
    solutionVersionId,
  });
};
